import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import OrderModal from "@/components/OrderModal";
import {
  Droplets,
  Leaf,
  ShieldCheck,
  Clock,
  Star,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  {
    icon: Droplets,
    title: "No Added Water",
    desc: "Pure fruit and vegetable juice, never diluted.",
  },
  {
    icon: Leaf,
    title: "No Added Sugar",
    desc: "Only natural sweetness from fresh produce.",
  },
  {
    icon: ShieldCheck,
    title: "No Preservatives",
    desc: "Clean ingredients, nothing artificial.",
  },
  {
    icon: Clock,
    title: "Crafted Fresh",
    desc: "Prepared just for you after you order.",
  },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [orderOpen, setOrderOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };
  const prevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const featuredProducts = products.slice(0, 3);

  return (
    <main data-testid="home-page">
      {/* ========== HERO ========== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: "#FCF8F6" }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_zumo-fresh-juice/artifacts/kz4f0njg_sand%202%20test%202%20extended%201.jpeg"
            alt="Zumo Company cold-pressed juice bottles on sand"
            className="w-full h-full object-cover"
          />
          {/* Desktop overlay */}
          <div className="hidden md:block absolute inset-0 hero-overlay" />
          {/* Mobile overlay */}
          <div className="md:hidden absolute inset-0 hero-overlay-mobile" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 pb-20 md:pt-0 md:pb-0">
          <div className="max-w-xl">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-6 animate-fade-up stagger-1"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#6F5E58",
              }}
            >
              Freshly Pressed in Qatar
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-none mb-6 animate-fade-up stagger-2"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#283618",
              }}
            >
              Pure Juice,
              <br />
              <span style={{ color: "#F0B7A4" }}>Nothing Else</span>
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed mb-10 max-w-md animate-fade-up stagger-3"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#6F5E58",
              }}
            >
              No Sugar. No Water. No Preservatives.
              <br />
              Just cold-pressed goodness, crafted fresh everyday.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-up stagger-4">
              <Link to="/products">
                <Button
                  data-testid="hero-explore-btn"
                  className="rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-300 hover:scale-105"
                  style={{
                    background: "#283618",
                    color: "#FCF8F6",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >
                  Explore Juices
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  data-testid="hero-about-btn"
                  variant="outline"
                  className="rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-300 hover:scale-105 border-2"
                  style={{
                    borderColor: "#283618",
                    color: "#283618",
                    background: "transparent",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >
                  Our Story
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 animate-fade-in stagger-5">
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-semibold"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#6F5E58" }}
          >
            Scroll
          </span>
          <div
            className="w-[1px] h-8"
            style={{ background: "#6F5E58" }}
          />
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section
        data-testid="features-section"
        className="py-24 md:py-32"
        style={{ background: "#FCF8F6" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16 md:mb-20">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#A3B18A",
              }}
            >
              Why Zumo
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {FEATURES.map((feat, i) => (
              <div
                key={feat.title}
                data-testid={`feature-card-${i}`}
                className="text-center group"
              >
                <div
                  className="feature-icon-ring w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                >
                  <feat.icon size={24} color="#283618" />
                </div>
                <h3
                  className="text-xl font-medium mb-3"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#2A2A2A",
                  }}
                >
                  {feat.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider max-w-5xl mx-auto" />

      {/* ========== FEATURED PRODUCTS ========== */}
      <section
        data-testid="featured-products-section"
        className="py-24 md:py-32"
        style={{ background: "#FCF8F6" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
            <div>
              <p
                className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  color: "#A3B18A",
                }}
              >
                Our Selection
              </p>
              <h2
                className="text-4xl md:text-5xl tracking-tight leading-tight font-medium"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "#283618",
                }}
              >
                Fresh Pressed Daily
              </h2>
            </div>
            <Link to="/products" className="mt-6 md:mt-0">
              <span
                className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors duration-300 hover:text-[#283618]"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  color: "#6F5E58",
                }}
              >
                View All <ArrowRight size={16} />
              </span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <div
                key={product.id}
                data-testid={`featured-product-${product.id}`}
                className="group product-card-hover"
              >
                <div
                  className="rounded-3xl overflow-hidden mb-5 aspect-[4/5]"
                  style={{ background: "#F5D5CB" }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-image w-full h-full object-cover transition-transform duration-700"
                  />
                </div>
                <div className="flex items-start justify-between">
                  <div>
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] font-semibold"
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        color: "#A3B18A",
                      }}
                    >
                      {product.category}
                    </span>
                    <h3
                      className="text-2xl font-medium mt-1"
                      style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: "#2A2A2A",
                      }}
                    >
                      {product.name}
                    </h3>
                  </div>
                  <span
                    className="text-sm font-semibold mt-1"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      color: "#283618",
                    }}
                  >
                    {product.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      {testimonials.length > 0 && (
        <section
          data-testid="testimonials-section"
          className="py-24 md:py-32"
          style={{ background: "#F5D5CB" }}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="text-center mb-16">
              <p
                className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  color: "#6F5E58",
                }}
              >
                What They Say
              </p>
              <h2
                className="text-4xl md:text-5xl tracking-tight leading-tight font-medium"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "#283618",
                }}
              >
                Loved by Doha
              </h2>
            </div>

            <div className="relative">
              {/* Testimonial Card */}
              <div
                className="text-center px-4 md:px-16"
                data-testid="active-testimonial"
              >
                <div className="flex justify-center gap-1 mb-6">
                  {Array.from({ length: testimonials[activeTestimonial]?.rating || 5 }).map(
                    (_, i) => (
                      <Star
                        key={i}
                        size={18}
                        fill="#F0B7A4"
                        color="#F0B7A4"
                      />
                    )
                  )}
                </div>
                <p
                  className="text-xl md:text-2xl leading-relaxed mb-8 italic"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#2A2A2A",
                  }}
                >
                  "{testimonials[activeTestimonial]?.text}"
                </p>
                <p
                  className="text-sm font-semibold uppercase tracking-[0.15em]"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  {testimonials[activeTestimonial]?.name}
                </p>
              </div>

              {/* Navigation */}
              <div className="flex justify-center gap-4 mt-10">
                <button
                  data-testid="testimonial-prev"
                  onClick={prevTestimonial}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{
                    background: "rgba(40, 54, 24, 0.1)",
                  }}
                >
                  <ChevronLeft size={20} color="#283618" />
                </button>
                <button
                  data-testid="testimonial-next"
                  onClick={nextTestimonial}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{
                    background: "rgba(40, 54, 24, 0.1)",
                  }}
                >
                  <ChevronRight size={20} color="#283618" />
                </button>
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`testimonial-dot-${i}`}
                    onClick={() => setActiveTestimonial(i)}
                    className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{
                      background:
                        i === activeTestimonial ? "#283618" : "rgba(40, 54, 24, 0.2)",
                      transform: i === activeTestimonial ? "scale(1.5)" : "scale(1)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== CTA ========== */}
      <section
        data-testid="cta-section"
        className="py-24 md:py-32 text-center"
        style={{ background: "#283618" }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight font-medium mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "#FCF8F6",
            }}
          >
            Ready to taste
            <br />
            the difference?
          </h2>
          <p
            className="text-base md:text-lg leading-relaxed mb-10 max-w-lg mx-auto"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: "rgba(252, 248, 246, 0.6)",
            }}
          >
            Order through our delivery partners and get fresh, cold-pressed juice delivered
            to your doorstep today.
          </p>
          <Button
              data-testid="cta-order-btn"
              onClick={() => setOrderOpen(true)}
              className="rounded-full px-10 py-3.5 text-sm font-semibold uppercase tracking-wider transition-all duration-300 hover:scale-105"
              style={{
                background: "#F0B7A4",
                color: "#283618",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Order Now
            </Button>
        </div>
      </section>
      <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
    </main>
  );
}
